/**
 * vizz.fm preset library \u2014 60 hand-translated presets covering every scene
 * variant from the vizz.fm app bundle (extracted 2026-05-04).
 *
 * Each entry maps a vizz.fm config to a PulseFX scene type + parameter
 * snapshot. The translation is intentionally lossy \u2014 we approximate the
 * essence (palette, intensity, layer mix, post-fx vibe) rather than try to
 * pixel-match a different engine. Recipients of these presets see something
 * visually in the spirit of the vizz.fm variant, rendered through the
 * PulseFX engine's stronger feature set (bloom, feedback, 16 layers, etc.).
 *
 * The library is the input to the "Preset Library" UI section. Pick a tile,
 * the entry's `apply()` function writes its scene type + params + bg to the
 * renderer in one call.
 */

import { BioclastParams, DEFAULT_BIOCLAST } from "../viz/scenes/bioclast";
import type { SceneType } from "../viz/renderer";
import type { BgConfig } from "../viz/background";
import { VIZZ_EXTRAS, VizCameraState } from "./vizzCameraStates";

export interface VizPreset {
  /** Display name (matches vizz.fm `name` field where possible). */
  name: string;
  /** vizz.fm config id, kept so users can cross-reference. */
  vizzId: string;
  /** Group label \u2014 matches the vizz.fm visualizer-picker categories. */
  group:
    | "Mesh Grid"
    | "Equalizer Rings"
    | "Wavy Form"
    | "Textured Sphere"
    | "Dot Grid"
    | "Particle Dance"
    | "Audio Tunnel"
    | "Frequency Hallway"
    | "Hidden / Dev";
  /** PulseFX scene archetype this maps to. */
  sceneType: SceneType;
  /** Param overrides applied on top of DEFAULT_BIOCLAST. */
  params: Partial<BioclastParams>;
  /** Background config to apply alongside the scene. */
  bg?: Partial<BgConfig>;
}

// ---------------------------------------------------------------------------
// Group palettes \u2014 each scene group settles into a recognizable color family
// derived from the vizz.fm originals so the library reads as cohesive rather
// than a kitchen sink.
// ---------------------------------------------------------------------------
const HUES = {
  meshGridSynthwave: 290,   // purples / magentas
  ringsRainbow: 220,
  wavySunset: 350,
  spherePink: 330,
  dotPastel: 280,
  particleGreen: 130,
  tunnelBlue: 200,
  hallwayPeach: 25,
  ditherCyan: 190,
  monjori: 110,
};

// ---------------------------------------------------------------------------
// Per-archetype factories \u2014 make a base config for a scene type, then each
// variant tweaks 3-6 fields. Keeps the library compact (would be 60 \u00d7 ~20
// lines otherwise).
// ---------------------------------------------------------------------------

function meshGrid(name: string, vizzId: string, p: Partial<BioclastParams> = {}, bg?: Partial<BgConfig>): VizPreset {
  return {
    name, vizzId, group: "Mesh Grid", sceneType: "meshGrid",
    params: {
      paletteHue: HUES.meshGridSynthwave, saturation: 0.85, intensity: 1.4,
      bassGravity: 1.6, midCurl: 1.0, trebleShimmer: 0.7, flowSpeed: 1.0,
      bloomStrength: 1.1, bloomRadius: 0.6, bloomThreshold: 0.15,
      cameraSpin: 0.4, cameraTilt: 0.6,
      ...p,
    },
    bg: bg ?? { style: "starfield", primary: "#0a0218", secondary: "#a02dc5", reactivity: 1.4 },
  };
}

function rings(name: string, vizzId: string, p: Partial<BioclastParams> = {}, bg?: Partial<BgConfig>): VizPreset {
  // "Equalizer Rings" \u2192 Bioclast with strong orbital + lattice layers.
  return {
    name, vizzId, group: "Equalizer Rings", sceneType: "bioclast",
    params: {
      paletteHue: HUES.ringsRainbow, saturation: 0.9, intensity: 1.6, starSize: 0.16,
      bassGravity: 1.5, midCurl: 1.0, trebleShimmer: 1.2, cymaticStrength: 2.2, flowSpeed: 0.9,
      cameraTilt: 0.85, cameraZoom: 0.85,
      layerParticles: false, layerOrbital: true, layerLattice: true, layerVortex: true,
      layerStrings: false, layerWarp: false, layerCathedral: false, layerDnaHelix: false,
      bloomStrength: 1.2, bloomRadius: 0.65, bloomThreshold: 0.1,
      ...p,
    },
    bg: bg ?? { style: "starfield", primary: "#000000", secondary: "#88aaff", reactivity: 1.2 },
  };
}

function wavy(name: string, vizzId: string, p: Partial<BioclastParams> = {}, bg?: Partial<BgConfig>): VizPreset {
  return {
    name, vizzId, group: "Wavy Form", sceneType: "wavyForm",
    params: {
      paletteHue: HUES.wavySunset, saturation: 0.85, intensity: 1.3,
      bassGravity: 1.4, midCurl: 1.0, trebleShimmer: 1.0, flowSpeed: 1.1,
      bloomStrength: 1.3, bloomRadius: 0.7, bloomThreshold: 0.12,
      cameraSpin: 0.3, cameraTilt: 0.4,
      ...p,
    },
    bg: bg ?? { style: "linear", primary: "#2c0f0f", secondary: "#2a2a3e", reactivity: 0.8 },
  };
}

function sphere(name: string, vizzId: string, p: Partial<BioclastParams> = {}, bg?: Partial<BgConfig>): VizPreset {
  // "Textured Sphere" — dedicated wireframe-icosphere scene with per-vertex
  // audio-displaced spikes (vizz.fm `audioReactiveSphere` parity).
  // Vizz sphere configs do NOT enable bloom by default — keep it low so the
  // black core + colored spikes stay visible instead of blooming to white.
  return {
    name, vizzId, group: "Textured Sphere", sceneType: "texturedSphere",
    params: {
      paletteHue: HUES.spherePink, saturation: 0.95, intensity: 1.4,
      bassGravity: 1.7, midCurl: 0.8, trebleShimmer: 1.3, flowSpeed: 0.8,
      cameraSpin: 0.5,
      bloomEnabled: false, bloomStrength: 0, bloomRadius: 0.4, bloomThreshold: 0.6,
      ...p,
    },
    bg: bg ?? { style: "solid", primary: "#000000", secondary: "#000000", reactivity: 0.6 },
  };
}

function dotGrid(name: string, vizzId: string, p: Partial<BioclastParams> = {}, bg?: Partial<BgConfig>): VizPreset {
  return {
    name, vizzId, group: "Dot Grid", sceneType: "dotGrid",
    params: {
      paletteHue: HUES.dotPastel, saturation: 0.7, intensity: 1.2,
      bassGravity: 1.5, midCurl: 0.8, trebleShimmer: 0.9, flowSpeed: 0.9,
      cameraSpin: 0.3, cameraTilt: 0.0,
      bloomStrength: 0.9, bloomRadius: 0.5, bloomThreshold: 0.2,
      ...p,
    },
    bg: bg ?? { style: "solid", primary: "#000000", secondary: "#000000", reactivity: 0.5 },
  };
}

function particle(name: string, vizzId: string, p: Partial<BioclastParams> = {}, bg?: Partial<BgConfig>): VizPreset {
  return {
    name, vizzId, group: "Particle Dance", sceneType: "bioclast",
    params: {
      paletteHue: HUES.particleGreen, saturation: 0.85, intensity: 1.4, starSize: 0.24,
      bassGravity: 1.3, midCurl: 1.4, trebleShimmer: 1.1, cymaticStrength: 1.0, flowSpeed: 1.2,
      cameraTilt: 0.5, cameraZoom: 1.0, cameraSpin: 0.5,
      layerParticles: true, layerStrings: true, layerBoids: true,
      layerLattice: false, layerOrbital: false, layerVortex: false, layerWarp: false,
      bloomStrength: 1.0, bloomRadius: 0.55, bloomThreshold: 0.18,
      ...p,
    },
    bg: bg ?? { style: "starfield", primary: "#000000", secondary: "#71e577", reactivity: 1.6 },
  };
}

function tunnel(name: string, vizzId: string, p: Partial<BioclastParams> = {}, bg?: Partial<BgConfig>): VizPreset {
  return {
    name, vizzId, group: "Audio Tunnel", sceneType: "bioclast",
    params: {
      paletteHue: HUES.tunnelBlue, saturation: 0.85, intensity: 1.6, starSize: 0.14,
      bassGravity: 1.6, midCurl: 1.2, trebleShimmer: 1.4, cymaticStrength: 1.0, flowSpeed: 1.5,
      cameraMode: "flythrough", flythroughSpeed: 1.4, beatDolly: 0.6,
      cameraTilt: 0.5, cameraZoom: 1.0,
      layerParticles: true, layerWarp: true, layerVortex: true, layerWind: true,
      layerLattice: false, layerOrbital: false, layerCathedral: false,
      bloomStrength: 1.4, bloomRadius: 0.7, bloomThreshold: 0.1,
      fxBarrel: 0.4,
      ...p,
    },
    bg: bg ?? { style: "radial", primary: "#000000", secondary: "#001a44", reactivity: 1.2 },
  };
}

function hallway(name: string, vizzId: string, p: Partial<BioclastParams> = {}, bg?: Partial<BgConfig>): VizPreset {
  return {
    name, vizzId, group: "Frequency Hallway", sceneType: "frequencyBars",
    params: {
      paletteHue: HUES.hallwayPeach, saturation: 0.8, intensity: 1.4,
      bassGravity: 1.6, midCurl: 1.0, trebleShimmer: 1.0, flowSpeed: 0.9,
      cameraSpin: 0.4, cameraTilt: 0.4,
      bloomStrength: 1.1, bloomRadius: 0.6, bloomThreshold: 0.18,
      ...p,
    },
    bg: bg ?? { style: "linear", primary: "#000000", secondary: "#19197c", reactivity: 1.0 },
  };
}

function dither(name: string, vizzId: string, p: Partial<BioclastParams> = {}, bg?: Partial<BgConfig>): VizPreset {
  // No native dither-pulse-ring scene yet \u2014 approximate with vortex + heavy dither post-fx.
  return {
    name, vizzId, group: "Hidden / Dev", sceneType: "bioclast",
    params: {
      paletteHue: HUES.ditherCyan, saturation: 0.7, intensity: 1.2, starSize: 0.18,
      bassGravity: 1.0, midCurl: 0.8, trebleShimmer: 0.6, cymaticStrength: 2.4, flowSpeed: 0.6,
      cameraTilt: 0.7, cameraZoom: 1.0,
      layerParticles: false, layerVortex: true, layerLattice: true, layerOrbital: true,
      bloomStrength: 0.8, bloomRadius: 0.5, bloomThreshold: 0.25,
      fxDither: 0.7, fxRgbShift: 0.2, fxBarrel: 0.2,
      ...p,
    },
    bg: bg ?? { style: "starfield", primary: "#000000", secondary: "#a6efff", reactivity: 1.5 },
  };
}

// ---------------------------------------------------------------------------
// THE 60 PRESETS \u2014 in vizz.fm group order.
// ---------------------------------------------------------------------------
export const VIZZ_PRESETS: VizPreset[] = [
  // ---- Mesh Grid (8 variants) ----
  // V1 \u2014 Synthwave purple dome with radial waves emanating from centre.
  meshGrid("Mesh Grid v1 (Radial Dome)", "1741367309222-608", {
    paletteHue: 285, saturation: 0.95, intensity: 1.5,
    bassGravity: 1.6, cymaticStrength: 2.0,
    meshGridRadialMix: 1, meshGridBend: -0.6, meshGridCircleShape: true,
    meshGridNoiseAmount: 1.2,
    cameraTilt: 0.3, cameraZoom: 0.85,
  }, { style: "starfield", primary: "#02040a", secondary: "#a02dc5", reactivity: 1.4 }),

  // V2 (was Config 22) \u2014 white wireframe mountain on black, subtle.
  meshGrid("Config 22 (White Wireframe)", "1740884813034-660", {
    paletteHue: 0, saturation: 0, intensity: 0.95,
    bassGravity: 0.9, flowSpeed: 0.6, cymaticStrength: 0.7,
    meshGridRadialMix: 0.3, meshGridBend: -0.2, meshGridNoiseAmount: 0.9,
    fxTiltShift: 0.5,
  }, { style: "solid", primary: "#000000", secondary: "#000000", reactivity: 0.4 }),

  // V3 \u2014 THREE STACKED slabs (vizz gridTile_rows=3). The unique 3-tier look.
  meshGrid("Mesh Grid 20:22:49 (3 Stacked)", "1770772969796-306", {
    paletteHue: 165, saturation: 0.6, intensity: 1.3,
    bassGravity: 1.4, cymaticStrength: 1.0, flowSpeed: 0.9,
    meshGridRadialMix: 0.7, meshGridBend: 0.4, meshGridCircleShape: true,
    meshGridNoiseAmount: 1.4,
    meshGridYStacks: 3, meshGridYStackSpacing: 22, meshGridYStackHueShift: 8,
    cameraTilt: 0.2, cameraZoom: 0.7,
  }, { style: "linear", primary: "#000000", secondary: "#000000", reactivity: 0.5 }),

  // V4 \u2014 Bowl shape (radialMix=1, bend up, audio rippling outward).
  meshGrid("Mesh Grid 21:00:05 (Bowl)", "1770775205855-545", {
    paletteHue: 290, saturation: 0.85, intensity: 1.5,
    bassGravity: 1.8, cymaticStrength: 2.4,
    meshGridRadialMix: 1, meshGridBend: 0.8, meshGridCircleShape: true,
    meshGridNoiseAmount: 1.8,
    cameraTilt: 0.45, cameraZoom: 0.85,
  }, { style: "starfield", primary: "#0a0218", secondary: "#a02dc5", reactivity: 1.4 }),

  // V5 \u2014 Mesh mountain (heavy noise, no radial mix, side-on view).
  meshGrid("Mesh Grid 21:09:37 (Mountain)", "1770775777921-139", {
    paletteHue: 130, saturation: 0.85, intensity: 1.4,
    bassGravity: 1.5, cymaticStrength: 1.2,
    meshGridRadialMix: 0, meshGridBend: -1.2, meshGridCircleShape: true,
    meshGridNoiseAmount: 4.0,
    cameraTilt: 0.15, cameraZoom: 0.8,
    fxBarrel: -0.2,
  }, { style: "starfield", primary: "#000000", secondary: "#88ff88", reactivity: 1.6 }),

  // V6 \u2014 Kaleidoscope rainbow bowl (vizz Mesh Grid 22:04:41 with kaleido + sepia).
  meshGrid("Mesh Grid 22:04:41 (Kaleido Rainbow)", "1770951881130-725", {
    paletteHue: 0, saturation: 1.0, intensity: 1.7,
    bassGravity: 1.6, cymaticStrength: 2.5, trebleShimmer: 1.5,
    meshGridRadialMix: 1, meshGridBend: 0.6, meshGridCircleShape: true,
    meshGridNoiseAmount: 2.5, meshGridYStackHueShift: 60,
    cameraTilt: 0.3, cameraZoom: 0.7,
    fxKaleidoscope: 0.6, fxKaleidoscopeSegments: 4, fxSwirl: 0.2,
  }, { style: "solid", primary: "#1a0530", secondary: "#1a0530", reactivity: 0.5 }),

  // V7 \u2014 Tall mountain with treble-driven peaks (Mesh Grid 14:48:57).
  meshGrid("Mesh Grid 14:48:57 (Spiky Mountain)", "1771357737340-827", {
    paletteHue: 200, saturation: 0.85, intensity: 1.6,
    bassGravity: 2.0, trebleShimmer: 1.8, cymaticStrength: 2.5,
    meshGridRadialMix: 0.2, meshGridBend: -1.6, meshGridCircleShape: true,
    meshGridNoiseAmount: 5.0,
    cameraTilt: 0.1, cameraZoom: 0.9,
  }, { style: "starfield", primary: "#000000", secondary: "#aaccff", reactivity: 1.6 }),

  // V8 \u2014 Tiled with kaleidoscope + barrel (Mesh Grid 13:01:29).
  meshGrid("Mesh Grid 13:01:29 (Tiled Kaleido)", "1771264889154-560", {
    paletteHue: 290, saturation: 0.9, intensity: 1.5,
    bassGravity: 1.7,
    meshGridRadialMix: 0.6, meshGridBend: 0.3, meshGridCircleShape: true,
    meshGridNoiseAmount: 2.0,
    fxGridTile: 0.6, fxGridTileColumns: 2,
    fxKaleidoscope: 0.4, fxKaleidoscopeSegments: 2, fxBarrel: -0.2,
  }),

  // ---- Equalizer Rings (8 variants) ----
  rings("Top Down Ripple 5", "1743375318790-786", {
    cameraTilt: 1.0, cymaticStrength: 2.5, layerWaveform: true, waveformStyle: "radial",
  }),
  rings("Config 24", "1740885088308-568", {
    paletteHue: 200, intensity: 1.8, cymaticStrength: 2.8, fieldTightness: 1.3,
    layerVortex: true, layerOrbital: true,
  }),
  rings("Config 15", "1741125155064-588", {
    paletteHue: 215, saturation: 0.7, intensity: 1.4,
  }, { style: "linear", primary: "#3e587f", secondary: "#000000", reactivity: 1.0 }),
  rings("Orbit Balls", "1740976813729-306", {
    paletteHue: 0, intensity: 1.6, cameraTilt: 0.4, cameraZoom: 0.7,
  }, { style: "solid", primary: "#0b0b0b", secondary: "#0b0b0b", reactivity: 0.5 }),
  rings("Config 17", "1741126975458-255", {
    paletteHue: 220, intensity: 2.0, cymaticStrength: 3.0,
  }, { style: "solid", primary: "#002f62", secondary: "#000000", reactivity: 0.8 }),
  rings("Top Down Ripple 6 (a)", "1768006730471-609", {
    paletteHue: 25, cameraTilt: 0.95, layerOrbital: true, layerLattice: true,
  }, { style: "starfield", primary: "#5c3c3c", secondary: "#ffaa66", reactivity: 1.5 }),
  rings("Top Down Ripple 6 (b)", "1766944199281-948", {
    paletteHue: 0, saturation: 0, intensity: 1.7, fieldTightness: 0.8,
  }, { style: "plasma", primary: "#000000", secondary: "#444444", reactivity: 1.6 }),
  rings("Orbital Spheres Shader", "1770589865591-184", {
    paletteHue: 0, intensity: 1.5, cymaticStrength: 1.5, fxBarrel: -0.4,
  }, { style: "starfield", primary: "#000000", secondary: "#ff4444", reactivity: 1.6 }),

  // ---- Wavy Form (9 variants) ----
  wavy("Waveform V2 3-new", "1768065577705-516", {
    paletteHue: 215, saturation: 0.9, intensity: 1.2, bloomStrength: 1.0,
    midCurl: 1.4, flowSpeed: 1.2,
  }, { style: "linear", primary: "#001a3a", secondary: "#3366ff", reactivity: 1.0 }),
  wavy("Waveform V2 3-new2", "1768100078571-150", {
    bloomStrength: 1.6, intensity: 1.6, paletteHue: 350,
  }),
  wavy("Waveform D", "1742757266181-97", {
    paletteHue: 220, intensity: 0.9, midCurl: 1.6, flowSpeed: 0.7,
  }, { style: "solid", primary: "#000000", secondary: "#000000", reactivity: 0.4 }),
  wavy("Default", "1742751498260-29", {
    paletteHue: 0, saturation: 0.4, intensity: 1.0, bloomStrength: 0.8,
  }, { style: "solid", primary: "#2c0f0f", secondary: "#2c0f0f", reactivity: 0.4 }),
  wavy("Waveform Worm + C", "1740841276433-982", {
    paletteHue: 30, intensity: 1.4, fxBarrel: -0.4, midCurl: 1.5,
  }, { style: "linear", primary: "#141414", secondary: "#1a1a2e", reactivity: 0.7 }),
  wavy("Waveform V2 3xxx", "1768006208680-844", {
    paletteHue: 130, saturation: 0.85, midCurl: 1.0, intensity: 1.4, bloomStrength: 0.8,
    fxRadialBlur: 0.25,
  }, { style: "starfield", primary: "#001a0a", secondary: "#66ff99", reactivity: 1.2 }),
  wavy("Waveform V2 5", "1767919515755-585", {
    paletteHue: 0, intensity: 1.5, bloomStrength: 2.0, flowSpeed: 1.4,
    fxKaleidoscope: 0.2, // adds the spiral wraparound feel
  }),
  wavy("Waveform V2 4", "1767905700635-173", {
    paletteHue: 280, intensity: 1.3, fxBarrel: -0.5, camZoomAmount: 0.4, camZoomPeriod: 31,
  }, { style: "linear", primary: "#000000", secondary: "#1a1a2e", reactivity: 0.8 }),
  wavy("Waveform V2 20:46:15", "1770774375417-948", {
    paletteHue: 140, intensity: 1.4,
    camAutoRotateH: 0.0009, camAutoRoll: 0.0007, camFovAmount: 4, camFovPeriod: 34,
  }, { style: "linear", primary: "#a27373", secondary: "#2d6942", reactivity: 0.9 }),

  // ---- Textured Sphere (6 variants) — vizz-true values from bundle ----
  // V1 (Config 28) — flagship rainbow urchin with strong barrel pinch.
  sphere("Config 28 (Urchin)", "1741402612225-728", {
    sphereDetail: 200, sphereSize: 2.7, sphereDisplacement: 0.2,
    sphereVertexAudioMix: 0.4, sphereWaveFrequency: 0.5, sphereWaveSpeed: 0,
    sphereNoiseScale: 3.1, sphereNoiseIntensity: 0.4, sphereNoiseSpeed: 0.2,
    sphereColorIntensity: 2.7, sphereMotionSmoothing: 0.7, sphereRotationSpeed: 0,
    sphereBaseColor: "#000000", sphereSolidInterior: true,
    fxBarrel: -1, fxBarrelAmountX: 1.8, fxBarrelZoom: 0.68, fxBarrelOpacity: 1,
    fxSwirl: 0.001, fxSwirlAngle: 0,
    paletteHue: 340, saturation: 1.0, bassGravity: 1.4, trebleShimmer: 1.4,
  }, { style: "solid", primary: "#000000", secondary: "#000000", reactivity: 0.5 }),
  // V2 (Config 54) — same shape, deeper displacement + brighter colors.
  sphere("Config 54 (Big Urchin)", "1770081484705-37", {
    sphereDetail: 200, sphereSize: 2.7, sphereDisplacement: 0.3,
    sphereWaveFrequency: 0.5, sphereWaveSpeed: 0,
    sphereNoiseScale: 2.6, sphereNoiseIntensity: 0.4, sphereNoiseSpeed: 0.32,
    sphereColorIntensity: 5, sphereMotionSmoothing: 0.6, sphereRotationSpeed: 0,
    sphereBaseColor: "#000000", sphereSolidInterior: true,
    fxBarrel: -1, fxBarrelAmountX: 1.8, fxBarrelZoom: 0.68, fxBarrelOpacity: 1,
    fxSwirl: 0.001, fxSwirlAngle: 0,
    paletteHue: 340, bassGravity: 1.6, trebleShimmer: 1.8,
  }, { style: "linear", primary: "#000000", secondary: "#1a1a2e", reactivity: 0.6 }),
  // V3 (Sphere Mesh) — soft green pollen-grain with high wave frequency.
  sphere("Sphere Mesh (Soft Green)", "1741397084495-830", {
    sphereDetail: 64, sphereSize: 1.7, sphereDisplacement: 0.4,
    sphereVertexAudioMix: 0.05, sphereWaveFrequency: 8, sphereWaveSpeed: 0,
    sphereNoiseScale: 0.2, sphereNoiseIntensity: 0.55, sphereNoiseSpeed: 0.15,
    sphereColorIntensity: 2.9, sphereMotionSmoothing: 0.95,
    sphereBaseColor: "#9cf5ad",
    paletteHue: 130, saturation: 0.55, bassGravity: 1.0, trebleShimmer: 0.7,
    fxTiltShift: 0.4, fxFilmGrain: 0.25,
  }, { style: "linear", primary: "#000000", secondary: "#595959", reactivity: 0.7 }),
  // V4 (08:40:54) — high-detail starfield variant, very smooth motion.
  sphere("Sphere Mesh 08:40:54 (Starfield)", "1770903654916-304", {
    sphereDetail: 124, sphereDisplacement: 0.1, sphereVertexAudioMix: 1,
    sphereWaveFrequency: 0.5, sphereWaveSpeed: 0,
    sphereNoiseScale: 5, sphereNoiseIntensity: 0.35, sphereNoiseSpeed: 0.14,
    sphereColorIntensity: 5, sphereMotionSmoothing: 0.95,
    sphereBaseColor: "#000000", sphereSolidInterior: true,
    paletteHue: 0, bassGravity: 1.8, trebleShimmer: 1.5,
    camFovAmount: 3, camFovPeriod: 26,
  }, { style: "starfield", primary: "#000000", secondary: "#ff8080", reactivity: 2.0 }),
  // V5 (08:11:48) — low noise scale = giant slow blobs, slowly rotating.
  sphere("Sphere Mesh 08:11:48 (Slow Bloom)", "1770901908988-358", {
    sphereDetail: 80, sphereSize: 0.9, sphereDisplacement: 0.2,
    sphereWaveFrequency: 0.5, sphereWaveSpeed: 0,
    sphereNoiseScale: 0.4, sphereNoiseIntensity: 0.5, sphereNoiseSpeed: 0.14,
    sphereColorIntensity: 5, sphereMotionSmoothing: 0, sphereRotationSpeed: 0.1,
    sphereBaseColor: "#000000",
    paletteHue: 210, bassGravity: 1.5, trebleShimmer: 1.0,
    cameraSpin: 0.4, camFovAmount: 5, camFovPeriod: 23,
  }, { style: "linear", primary: "#000000", secondary: "#1a1a2e", reactivity: 0.8 }),
  // V6 (08:01:24) — micro-noise smooth sphere, cinematic pan/zoom.
  sphere("Sphere Mesh 08:01:24 (Cinematic)", "1770901284990-863", {
    sphereDetail: 124, sphereDisplacement: 0.1, sphereVertexAudioMix: 1,
    sphereWaveFrequency: 0.5, sphereWaveSpeed: 0,
    sphereNoiseScale: 0.1, sphereNoiseIntensity: 0.35, sphereNoiseSpeed: 0.14,
    sphereColorIntensity: 5, sphereMotionSmoothing: 0.6,
    sphereBaseColor: "#000000",
    paletteHue: 280, bassGravity: 1.4, trebleShimmer: 1.3,
    camZoomAmount: 0.6, camZoomPeriod: 25, camFovAmount: 3, camPanAmount: 0.3,
  }, { style: "linear", primary: "#000000", secondary: "#1a1a2e", reactivity: 0.7 }),

  // ---- Dot Grid (6 variants) ----
  dotGrid("Dot Grid", "1766283127041-472", {
    fxBarrel: -0.35,
  }),
  dotGrid("Dot Grid 66", "1767875526367-77", {
    paletteHue: 0, saturation: 0.4, intensity: 1.4, bassGravity: 1.7,
  }),
  dotGrid("Dot Grid 5", "1766441015772-83", {
    intensity: 1.5, camZoomAmount: 0.7, camZoomPeriod: 34,
    fxKaleidoscope: 0.3, fxKaleidoscopeSegments: 6,
  }),
  dotGrid("Dot Grid 3", "1766286388222-783", {
    paletteHue: 30, intensity: 1.3,
  }),
  dotGrid("Dot Grid 2", "1766284757815-797", {
    paletteHue: 200, intensity: 1.6, bassGravity: 2.0,
  }),
  dotGrid("Dot Grid 21:09:12", "1770516552579-966", {
    paletteHue: 320, fxKaleidoscope: 0.4, fxKaleidoscopeSegments: 6,
  }),

  // ---- Particle Dance (4 variants) ----
  particle("Particle Dance 14:18:13", "1770146293356-684", {
    paletteHue: 50, saturation: 0.9, intensity: 1.5,
    bloomStrength: 1.4, fxFilmGrain: 0.3,
  }, { style: "linear", primary: "#1a1500", secondary: "#ffcc44", reactivity: 1.4 }),
  particle("Particle Dance 18:13:47 (a)", "1770160427378-550", {
    paletteHue: 130, fxBarrel: 0.3, fxSwirl: 0.4,
  }),
  particle("Particle Dance 18:13:47 (b)", "1770417368156-780", {
    paletteHue: 130, intensity: 1.6, fxBarrel: -0.5, fxTiltShift: 0.5,
  }),
  particle("Particle Dance 20:50:36", "1770774636923-484", {
    paletteHue: 260, fxBarrel: -0.3, fxRadialBlur: 0.2,
  }, { style: "starfield", primary: "#918fb4", secondary: "#ffffff", reactivity: 2.0 }),

  // ---- Audio Tunnel (4 variants) ----
  tunnel("Tunnel3", "1741224914328-496"),
  tunnel("TunneCompressed2", "1741317030272-588", {
    flythroughSpeed: 0.8, beatDolly: 0.4, intensity: 1.3, fxBarrel: -0.4,
  }),
  tunnel("Tunnel4", "1741226809921-631", {
    paletteHue: 320, saturation: 0.95,
    flythroughSpeed: 1.8, fxRadialBlur: 0.45, fxRgbShift: 0.35,
    cameraTilt: 0.7, beatDolly: 0.7,
  }, { style: "starfield", primary: "#1a001a", secondary: "#ff66cc", reactivity: 1.7 }),
  tunnel("Tunnel", "1741216558355-643", {
    flythroughSpeed: 1.0, layerWind: false, layerVortex: false,
  }, { style: "starfield", primary: "#000000", secondary: "#88ccff", reactivity: 1.4 }),

  // ---- Frequency Hallway (4 variants) ----
  hallway("Hallway", "1740974173999-504"),
  hallway("FreqBarz", "1740927577330-407", {
    paletteHue: 0, saturation: 0.1, intensity: 0.7, bloomStrength: 1.4,
    fxFilmGrain: 0.4,
  }, { style: "starfield", primary: "#3f3e75", secondary: "#c2c2c2", reactivity: 2.0 }),
  hallway("Frequency Bars 19:46:48", "1770770808304-618", {
    paletteHue: 0, saturation: 0.1, intensity: 1.4,
    camZoomAmount: 0.3, camZoomPeriod: 33, camFovAmount: 8, camFovPeriod: 21,
    fxBarrel: -0.5,
  }),
  hallway("Frequency Bars 20:11:29", "1770772289183-3", {
    paletteHue: 240, intensity: 1.2,
    camFovAmount: 3, camFovPeriod: 19, camPanAmount: 0.2,
    fxFilmGrain: 0.4,
  }, { style: "linear", primary: "#4f4f4f", secondary: "#19197c", reactivity: 1.0 }),

  // ---- Hidden / Dev (10 extras from the bundle) ----
  // The 5 dither-pulse-ring variants in the bundle differ by tiny fx amounts;
  // we re-tune each one to a distinct hue + post-fx character so they read as
  // genuinely different presets instead of near-clones.
  dither("Dither Pulse Ring 17:31:29", "1771713089860-5", {
    paletteHue: 190, fxDither: 0.7, fxRgbShift: 0.2, fxRadialBlur: 0.0,
    cymaticStrength: 2.4, fxBarrel: 0.0,
  }, { style: "starfield", primary: "#000000", secondary: "#a6efff", reactivity: 1.5 }),
  dither("Dither Pulse Ring 15:08:00", "1771704480182-761", {
    paletteHue: 290, saturation: 0.85, intensity: 1.5,
    cymaticStrength: 1.8, fxDither: 0.5, fxRadialBlur: 0.45,
    fxKaleidoscope: 0.35, fxKaleidoscopeSegments: 6,
  }, { style: "plasma", primary: "#10001a", secondary: "#ff66ff", reactivity: 1.6 }),
  dither("Dither Pulse Ring 14:21:53", "1771701713431-70", {
    paletteHue: 30, saturation: 0.9, intensity: 1.7,
    fxDither: 0.6, cymaticStrength: 2.0, fxFilmGrain: 0.5, fxVignette: 0.4,
  }, { style: "radial", primary: "#1a0a00", secondary: "#ffaa44", reactivity: 1.3 }),
  dither("Dither Pulse Ring 13:22:25", "1771611745969-129", {
    paletteHue: 130, saturation: 0.7, intensity: 1.3,
    fxDither: 0.8, fxPixelate: 0.4, fxBarrel: -0.5,
    layerVortex: true, layerOrbital: true, layerLattice: false,
  }, { style: "solid", primary: "#000000", secondary: "#000000", reactivity: 0.4 }),
  dither("Dither Pulse Ring 17:32:16", "1771713136313-856", {
    paletteHue: 340, saturation: 1.0, intensity: 1.6,
    fxDither: 0.45, fxRgbShift: 0.6, fxCrt: 0.55, fxGlitch: 0.25,
  }, { style: "linear", primary: "#1a001a", secondary: "#ff2266", reactivity: 1.7 }),
  // Disambiguate Waveform V2 trio — same scene, different palette + camera.
  wavy("Waveform V2 07:55:10", "1771505710720-276", {
    paletteHue: 5, intensity: 1.4, bloomStrength: 0.9,
    camFovAmount: 6, camFovPeriod: 22,
  }),
  sphere("Sphere Mesh 16:17:36", "1771190256201-133", {
    sphereDetail: 200, sphereSize: 1.2, sphereDisplacement: 0.1,
    sphereWaveFrequency: 0.5, sphereWaveSpeed: 0,
    sphereNoiseScale: 2.5, sphereNoiseIntensity: 0.4, sphereNoiseSpeed: 0.32,
    sphereColorIntensity: 2.4, sphereMotionSmoothing: 0.6, sphereRotationSpeed: 0,
    sphereBaseColor: "#000000", sphereSolidInterior: true,
    fxBarrel: -1, fxBarrelAmountX: 1.8, fxBarrelZoom: 0.68,
    fxSwirl: 0.001, fxSwirlAngle: 0,
    paletteHue: 200, saturation: 0.85, bassGravity: 1.4, trebleShimmer: 1.2,
  }, { style: "linear", primary: "#000000", secondary: "#1a1a2e", reactivity: 0.6 }),
  // Monjori \u2014 Shadertoy plasma look. Approximate with cathedral + aurora layers + plasma bg.
  {
    name: "Monjori 10:25:38", vizzId: "1770132338795-391", group: "Hidden / Dev", sceneType: "bioclast",
    params: {
      paletteHue: HUES.monjori, saturation: 0.85, intensity: 1.4, starSize: 0.18,
      bassGravity: 1.0, midCurl: 1.5, trebleShimmer: 1.2, flowSpeed: 1.6,
      layerParticles: true, layerAurora: true, layerCathedral: true, layerStrings: true,
      layerLattice: false, layerOrbital: false, layerWarp: false, layerVortex: false,
      bloomStrength: 1.5, bloomRadius: 0.7, bloomThreshold: 0.1,
      fxBarrel: -0.35,
    },
    bg: { style: "plasma", primary: "#1a1a2e", secondary: "#87df5c", reactivity: 1.5 },
  },
  wavy("Waveform V2 2 o", "1768016353948-778", {
    paletteHue: 200, intensity: 1.0, midCurl: 1.3,
    fxBarrel: 0.35, camPanAmount: 0.3, camPanPeriod: 18,
  }, { style: "linear", primary: "#001a2a", secondary: "#446688", reactivity: 0.8 }),
  wavy("Waveform V2 2", "1767904528356-841", {
    paletteHue: 50, saturation: 0.95, intensity: 1.3, bloomStrength: 1.6,
    fxKaleidoscope: 0.3, fxKaleidoscopeSegments: 4,
  }, { style: "radial", primary: "#1a0f00", secondary: "#ffcc44", reactivity: 1.0 }),
  wavy("Waveform V2", "1767904459912-352", {
    paletteHue: 280, saturation: 0.85, intensity: 1.1, bloomStrength: 1.1,
    fxTiltShift: 0.5, fxFilmGrain: 0.35,
  }, { style: "plasma", primary: "#0a0010", secondary: "#aa66ff", reactivity: 1.2 }),
];

/**
 * Apply a preset to the live renderer + background. Returns the merged
 * params object that was written, so the caller can refresh dependent UIs.
 *
 * `include` controls *what* the preset writes. Defaults are conservative —
 * only the LOOK is applied. Caller opts in to scene/camera/bg/fx so vizz
 * presets layer cleanly on top of the user's manual choices instead of
 * overwriting them.
 *
 * The traditional "vizz exact" workflow is `include: { look:true, scene:true,
 * camera:true, bg:true, fx:true }` plus `mode: "replace"`.
 */
export interface ApplyOptions {
  /** When "replace": overwrite EVERY param with DEFAULT_BIOCLAST first.
   *  When "layer" (default): only write the keys this preset defines. */
  mode?: "layer" | "replace";
  include?: {
    /** Look & mood numerics: palette, intensity, layer toggles, etc. */
    look?: boolean;
    /** Scene archetype (Bioclast vs Mesh Grid vs Sphere etc.). */
    scene?: boolean;
    /** Camera framing (vizz cameraState quaternion + target). */
    camera?: boolean;
    /** Background style + colors. */
    bg?: boolean;
    /** Post-FX stack values. */
    fx?: boolean;
  };
}

const DEFAULT_INCLUDE = { look: true, scene: false, camera: false, bg: false, fx: false };

/** Param-name prefixes that classify into camera / fx groups. */
const FX_PREFIX = "fx";
const CAM_PREFIX = "cam";
const SCENE_PREFIX = "sphere"; // sphereDetail/sphereSize/etc are scene-specific

function isFxKey(k: string): boolean {
  return (
    k.startsWith(FX_PREFIX) ||
    k.startsWith("bloom") ||
    k.startsWith("feedback") ||
    k.startsWith("godRays") ||
    k === "postFxEnabled"
  );
}
function isCamKey(k: string): boolean {
  return k.startsWith(CAM_PREFIX) || k === "cameraSpin" || k === "cameraZoom" || k === "cameraTilt" || k === "cameraOrbit" || k === "cameraMode" || k === "flythroughSpeed" || k === "beatDolly";
}
function isSceneSpecificKey(k: string): boolean {
  return k.startsWith(SCENE_PREFIX) || k.startsWith("meshGrid");
}

export function applyVizPreset(
  preset: VizPreset,
  rendererParams: BioclastParams,
  setSceneType: (t: SceneType) => void,
  setBg: ((bg: Partial<BgConfig>) => void) | null,
  setCameraState?: (cs: VizCameraState | null) => void,
  options: ApplyOptions = {},
): BioclastParams {
  const mode = options.mode ?? "layer";
  const inc = { ...DEFAULT_INCLUDE, ...(options.include ?? {}) };

  // Build the source params we will apply, filtered by include flags.
  const src: Record<string, unknown> = {};
  if (mode === "replace") {
    // Replace: start from defaults, then overlay preset.
    Object.assign(src, DEFAULT_BIOCLAST, preset.params);
  } else {
    Object.assign(src, preset.params);
  }
  // Apply with per-key filter so the user's manual look/fx/camera survive
  // when the corresponding include flag is off.
  const target = rendererParams as unknown as Record<string, unknown>;
  for (const k of Object.keys(src)) {
    const isFx = isFxKey(k);
    const isCam = isCamKey(k);
    const isSceneSpecific = isSceneSpecificKey(k);
    let allow = false;
    if (isFx) allow = !!inc.fx;
    else if (isCam) allow = !!inc.camera;
    else if (isSceneSpecific) allow = !!inc.scene;
    else allow = !!inc.look;
    if (allow) target[k] = src[k];
  }

  if (inc.scene) {
    setSceneType(preset.sceneType);
  }
  // Pull authentic vizz extras (cameraState + gridColor + bgSecondary).
  const extras = VIZZ_EXTRAS[preset.vizzId];
  if (inc.bg && setBg) {
    const baseBg: Partial<BgConfig> = {};
    if (extras?.bg) Object.assign(baseBg, extras.bg);
    if (extras?.bgSecondary && !baseBg.secondary) baseBg.secondary = extras.bgSecondary;
    const bg = { ...baseBg, ...(preset.bg || {}) };
    if (Object.keys(bg).length > 0) setBg(bg);
  }
  if (inc.scene && extras?.gridColor) {
    (target as Record<string, string>).meshGridLineColor = extras.gridColor;
  } else if (inc.scene && mode === "replace") {
    (target as Record<string, string>).meshGridLineColor = "";
  }
  if (inc.camera && setCameraState) {
    setCameraState(extras?.camera ?? null);
  } else if (!inc.camera && setCameraState) {
    // When camera is NOT being applied, clear any prior vizz lock so the
    // standard alt-camera rig (or Bioclast) controls the view.
    setCameraState(null);
  }
  return rendererParams;
}
